// Smart entity resolution (core-features 3.3 UX): spot misspelled team member
// names and @mentions in report text so a fix is one click, not a manual edit.

export interface Member { id: string; first: string; last: string; full: string }
export interface NameSuggestion { wrong: string; member: Member }
export interface Mention { raw: string; member: Member | null }

function distance(a: string, b: string): number {
  const m = a.length, n = b.length;
  const d = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 1; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return d[m][n];
}

// A capitalized word that isn't a roster name but is one or two edits from
// one (same first letter, mid-sentence) is probably that person, misheard.
export function nameSuggestions(text: string, roster: Member[]): NameSuggestion[] {
  const names = roster.flatMap((m) => [m.first, m.last].filter((x) => x.length >= 3).map((n) => ({ n, m })));
  const exact = new Set(names.map((x) => x.n.toLowerCase()));
  const out = new Map<string, NameSuggestion>();
  for (const hit of text.matchAll(/\b[A-Z][a-z]{2,}\b/g)) {
    const word = hit[0];
    const before = text.slice(0, hit.index).trimEnd();
    if (!before || /[.!?:]$/.test(before)) continue; // sentence-initial capitals aren't names
    if (text[hit.index! - 1] === "@") continue;
    const low = word.toLowerCase();
    if (exact.has(low) || out.has(word)) continue;
    let best: { d: number; m: Member } | null = null;
    for (const { n, m } of names) {
      const nl = n.toLowerCase();
      if (nl[0] !== low[0]) continue;
      const d = distance(low, nl);
      const limit = nl.length >= 6 ? 2 : 1;
      if (d > 0 && d <= limit && (!best || d < best.d)) best = { d, m };
    }
    if (best) out.set(word, { wrong: word, member: best.m });
  }
  return [...out.values()];
}

// @First or @First Last → the member the follow-up will route to.
export function mentions(text: string, roster: Member[]): Mention[] {
  const seen = new Set<string>();
  const out: Mention[] = [];
  for (const m of text.matchAll(/@([A-Z][a-z]+)(?:\s([A-Z][a-z]+))?/g)) {
    const [raw, first, last] = m;
    const member = roster.find((r) => r.first.toLowerCase() === first.toLowerCase() && (!last || r.last.toLowerCase() === last.toLowerCase()))
      ?? roster.find((r) => r.first.toLowerCase() === first.toLowerCase()) ?? null;
    const key = member?.id ?? raw;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ raw: last && !member?.last.toLowerCase().startsWith(last.toLowerCase()) ? `@${first}` : raw, member });
  }
  return out;
}

export function replaceWord(text: string, wrong: string, right: string) {
  return text.replace(new RegExp(`\\b${wrong}\\b`, "g"), right);
}
